import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
	CloseCircleOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	PlusCircleOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import { AsyncLink, PhoneLink } from 'components';
import { Page } from '../../components.js';
import { Client } from 'types';
import { Formatting } from '../../utils';
import { useClients, useFilters, useUsers } from 'reducers';
import styled from 'styled-components';

import '../../styles/pages/clients/clients.scss';
import { red } from '@ant-design/colors';

const Wrapper = styled.div`
	.ant-table-row {
		&.red {
			> td:first-child {
				border-left: 8px solid ${red[5]};
			}
		}
	}
`;

export const Clients: React.FC = () => {
	const navigate = useNavigate();

	const [filters, setFilters] = useFilters<{
		search: string | null;
		removed: string;
	}>({
		search: null,
		removed: '0',
	});
	const showRemoved = filters.removed === '1';

	const clients = useClients({
		search: filters.search ?? undefined,
		showRemoved,
	});
	const users = useUsers({});

	const columns: ColumnsType<Client> = [
		{
			title: 'Nazwa',
			key: 'name',
			render: (_, row) => row.name,
		},
		{
			title: 'Telefon',
			key: 'phone',
			render: (_, row) => <PhoneLink phone={row.phone} />,
		},
		{
			title: 'NIP',
			key: 'nip',
			render: (_, row) => row.nip,
		},
		{
			title: 'BDO',
			key: 'bdo',
			render: (_, row) => row.bdo,
		},
		{
			title: 'Email',
			key: 'email',
			render: (_, row) => row.email,
		},
		{
			title: showRemoved ? 'Usunięty - przez' : 'Dodany - przez',
			key: 'created',
			render: (_, row) => {
				if (showRemoved) {
					if (!row.removedBy || !row.removedDate) {
						return '-';
					}

					const removedBy = users?.find((user) => user.id === row.removedBy);
					return (
						<span>
							{Formatting.epochToDate(row.removedDate)} -{' '}
							<Link to={`/panel/drivers/${row.removedBy}`}>
								{removedBy?.name} {removedBy?.surname}
							</Link>
						</span>
					);
				}

				if (!row.createdBy || !row.createdDate) {
					return '-';
				}

				const createdBy = users?.find((user) => user.id === row.createdBy);
				return (
					<span>
						{Formatting.epochToDate(row.createdDate)} -{' '}
						<Link to={`/panel/drivers/${row.createdBy}`}>
							{createdBy?.name} {createdBy?.surname}
						</Link>
					</span>
				);
			},
		},
		{
			title: 'Akcje',
			key: 'action',
			render: (_, row) => {
				const actionName = row.removedBy ? 'Przywróć' : 'Usuń';
				const actionColor = row.removedBy ? '#52C41A' : '#f5222d';
				// const action = row.removedBy ? () => restoreClient(row.id) : () => deleteClient(row.id);
				const action = async () => {};

				return (
					<Space size={5}>
						<Link to={`/panel/clients/${row.id}`}>Szczegóły</Link>
						<span>|</span>
						<AsyncLink color={actionColor} onClick={action}>
							{actionName}
						</AsyncLink>
					</Space>
				);
			},
		},
	];

	const actions = [
		<Button
			key={0}
			type="primary"
			size="large"
			icon={<PlusCircleOutlined />}
			onClick={() => navigate('/panel/new-client')}
		>
			Dodaj nowego Klienta
		</Button>,
		<Button
			key={1}
			type="primary"
			size="large"
			icon={showRemoved ? <EyeInvisibleOutlined /> : <EyeOutlined />}
			onClick={() => setFilters('removed', showRemoved ? '0' : '1')}
		>
			{showRemoved ? 'Ukryj' : 'Pokaż'} usuniętych klientów
		</Button>,
	];

	return (
		<Page title="Klienci" actions={actions}>
			<Wrapper className="clients flex flex-dir flex-1">
				<div className="inputs flex">
					<Input
						placeholder="Wyszukaj"
						size="large"
						suffix={<SearchOutlined style={{ color: '#8C8C8C' }} />}
						value={filters.search ?? ''}
						onChange={(e) => setFilters('search', e.target.value || null)}
					/>
					<Button
						type="primary"
						size="large"
						icon={<CloseCircleOutlined />}
						onClick={() => {
							setFilters('removed', '0');
							setFilters('search', null);
						}}
					>
						Wyczyść
					</Button>
				</div>
				<Table
					columns={columns}
					rowClassName={(row) => (row.lockedDate !== null ? 'red' : '')}
					dataSource={clients ?? []}
					loading={!clients || !users}
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>
			</Wrapper>
		</Page>
	);
};
